import { DropdownOption, PaxRangeInputUnit } from "@ncino/pax-ui";
import { FilterType, MinMax } from "@/shared/components/Filters/types";
import { z } from "zod";

export const tabSchema = z.enum([
  "tearsheet",
  "analytics",
  "loan-list",
  "seller-institution",
  "due-diligence",
]);
export type TabValue = z.infer<typeof tabSchema>;

export const ASSET_CLASS_VALUES = [
  "AUTO",
  "CRE",
  "HELOC",
  "MORTGAGE",
  "UNSECURED",
  "BUSINESS",
] as const;
export const AUTO_LOAN_VALUES = ["DIRECT", "INDIRECT"] as const;
export const SETTLEMENT_TYPE_VALUES = ["PARTICIPATION", "WHOLE_LOAN"] as const;

const csvOf = (allowed: readonly string[]) =>
  z
    .string()
    .transform((value) => {
      const kept = value?.split(",").filter((v) => allowed.includes(v)) ?? [];
      return kept.length ? kept.join(",") : undefined;
    })
    .optional()
    .catch(undefined);

export const buyFilterSearchSchema = z.object({
  assetClass: csvOf(ASSET_CLASS_VALUES),
  autoLoans: csvOf(AUTO_LOAN_VALUES),
  settlementType: csvOf(SETTLEMENT_TYPE_VALUES),
  grossYield: z.string().optional().catch(undefined),
  price: z.string().optional().catch(undefined),
  poolSize: z.string().optional().catch(undefined),
  availableAmount: z.string().optional().catch(undefined),
  weightedAverageRate: z.string().optional().catch(undefined),
  weightedAverageLoanToValueRatio: z.string().optional().catch(undefined),
  weightedAverageFicoScore: z.string().optional().catch(undefined),
  weightedAverageRemainingLifeInMonths: z.string().optional().catch(undefined),
});
export type BuyFilterSearch = z.infer<typeof buyFilterSearchSchema>;

export type FilterValue = string[] | MinMax;

export type BuyFilterValues = {
  dealAttributes: {
    assetClass: string[];
    autoLoans: string[];
    settlementType: string[];
  };
  performance: {
    grossYield: MinMax;
    price: MinMax;
    poolSize: MinMax;
    availableAmount: MinMax;
  };
  creditQuality: {
    weightedAverageRate: MinMax;
    weightedAverageLoanToValueRatio: MinMax;
    weightedAverageFicoScore: MinMax;
    weightedAverageRemainingLifeInMonths: MinMax;
  };
};

export type BaseField = {
  key: string;
  title: string;
  titleShort?: string;
  type: FilterType;
  tooltip?: string;
};
export type SelectField = BaseField & {
  options: DropdownOption[];
};
export type RangeField = BaseField & { unit: PaxRangeInputUnit };

export type FilterField = SelectField | RangeField;

export type FilterSectionConfig = {
  key: keyof BuyFilterValues;
  title: string;
  fields: FilterField[];
};

export const DEFAULT_BUY_FILTERS: BuyFilterValues = {
  dealAttributes: { assetClass: [], autoLoans: [], settlementType: [] },
  performance: { grossYield: {}, price: {}, poolSize: {}, availableAmount: {} },
  creditQuality: {
    weightedAverageRate: {},
    weightedAverageLoanToValueRatio: {},
    weightedAverageFicoScore: {},
    weightedAverageRemainingLifeInMonths: {},
  },
};

const serializeArray = (values: string[]): string | undefined =>
  values.length ? values.join(",") : undefined;

const serializeRange = ({ min, max }: MinMax): string | undefined =>
  min == null && max == null ? undefined : `${min ?? ""}:${max ?? ""}`;

export function serializeFiltersToSearch(filters: BuyFilterValues): Partial<BuyFilterSearch> {
  const { dealAttributes, performance, creditQuality } = filters;
  return {
    assetClass: serializeArray(dealAttributes.assetClass),
    autoLoans: serializeArray(dealAttributes.autoLoans),
    settlementType: serializeArray(dealAttributes.settlementType),
    grossYield: serializeRange(performance.grossYield),
    price: serializeRange(performance.price),
    poolSize: serializeRange(performance.poolSize),
    availableAmount: serializeRange(performance.availableAmount),
    weightedAverageRate: serializeRange(creditQuality.weightedAverageRate),
    weightedAverageLoanToValueRatio: serializeRange(creditQuality.weightedAverageLoanToValueRatio),
    weightedAverageFicoScore: serializeRange(creditQuality.weightedAverageFicoScore),
    weightedAverageRemainingLifeInMonths: serializeRange(
      creditQuality.weightedAverageRemainingLifeInMonths
    ),
  };
}

const parseArray = (value: string | undefined): string[] =>
  value ? value.split(",").filter(Boolean) : [];

const toNum = (s: string): number | undefined => {
  if (!s) return undefined;
  const n = Number(s);
  return Number.isNaN(n) ? undefined : n;
};

const parseRange = (value: string | undefined): MinMax => {
  if (!value) return {};
  const [min, max] = value.split(":");
  const range = { min: toNum(min), max: toNum(max) };
  if (range.min !== undefined && range.max !== undefined && range.min > range.max) {
    return {};
  }
  return range;
};

export function parseFiltersFromSearch(search: BuyFilterSearch): BuyFilterValues | null {
  const filters: BuyFilterValues = {
    dealAttributes: {
      assetClass: parseArray(search.assetClass),
      autoLoans: parseArray(search.autoLoans),
      settlementType: parseArray(search.settlementType),
    },
    performance: {
      grossYield: parseRange(search.grossYield),
      price: parseRange(search.price),
      poolSize: parseRange(search.poolSize),
      availableAmount: parseRange(search.availableAmount),
    },
    creditQuality: {
      weightedAverageRate: parseRange(search.weightedAverageRate),
      weightedAverageLoanToValueRatio: parseRange(search.weightedAverageLoanToValueRatio),
      weightedAverageFicoScore: parseRange(search.weightedAverageFicoScore),
      weightedAverageRemainingLifeInMonths: parseRange(search.weightedAverageRemainingLifeInMonths),
    },
  };

  const hasAny = [filters.dealAttributes, filters.performance, filters.creditQuality].some(
    (section) =>
      Object.values(section).some((v) =>
        Array.isArray(v) ? v.length > 0 : v.min !== undefined || v.max !== undefined
      )
  );

  return hasAny ? filters : null;
}

export const cleanBrowseSearch = (raw: unknown): BuyFilterSearch =>
  serializeFiltersToSearch(
    parseFiltersFromSearch(buyFilterSearchSchema.parse(raw)) ?? DEFAULT_BUY_FILTERS
  );
